/**
 * SEO Section
 *
 * GA4 overview cards, Search Console metrics,
 * top queries table, top pages table, indexing coverage.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Eye, Users, TrendingUp, Search, Globe, FileCheck } from 'lucide-react';
import { StatCardSkeleton, ChartSkeleton } from './AnalyticsSkeleton';
import { useAuth } from '../../../contexts/AuthContext';

interface GA4Overview {
  pageviews: number;
  total_users: number;
  new_users: number;
  sessions: number;
  avg_session_duration: number;
  bounce_rate: number;
  engagement_rate: number;
}

interface SearchQuery {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

interface TopPage {
  path: string;
  title?: string;
  pageviews?: number;
  clicks: number;
  impressions: number;
  ctr: number;
  search_position?: number;
}

interface IndexingStatus {
  indexed: number;
  not_indexed: number;
  total_submitted?: number;
  coverage_rate?: number;
}

const API_URL =
  import.meta.env.VITE_API_URL ||
  'https://claude-essay-agent-production.up.railway.app';

export const SEOSection: React.FC = () => {
  const { getAuthToken } = useAuth();
  const [ga4, setGa4] = useState<GA4Overview | null>(null);
  const [queries, setQueries] = useState<SearchQuery[]>([]);
  const [pages, setPages] = useState<TopPage[]>([]);
  const [indexing, setIndexing] = useState<IndexingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    const fetchSEO = async () => {
      setLoading(true);
      try {
        const baseUrl = `${API_URL}/api/v1/analytics`;

        // These four were being fetched with no Authorization header at all.
        // That was unnoticed only because the endpoints were unauthenticated;
        // now they require credentials AND an admin role, because
        // GA4Client()/GSCClient() always read the operator's single global
        // property rather than the caller's, so serving them to a merchant
        // would show them someone else's traffic as their own.
        const token = await getAuthToken();
        if (!token) {
          setHasData(false);
          return;
        }
        const headers = { Authorization: `Bearer ${token}` };

        // 403 is the expected answer for a non-admin, so it must read as
        // "nothing to show" rather than as a failure.
        const fetchSeo = async (path: string) => {
          const res = await fetch(`${baseUrl}${path}`, { headers });
          if (!res.ok) throw new Error(String(res.status));
          return res.json();
        };

        const [ga4Res, queriesRes, pagesRes, indexingRes] = await Promise.allSettled([
          fetchSeo('/seo/ga4-overview'),
          fetchSeo('/seo/search-queries'),
          fetchSeo('/seo/top-pages'),
          fetchSeo('/seo/indexing-status'),
        ]);

        if (ga4Res.status === 'fulfilled') setGa4(ga4Res.value);
        if (queriesRes.status === 'fulfilled') setQueries(queriesRes.value);
        if (pagesRes.status === 'fulfilled') setPages(pagesRes.value);
        if (indexingRes.status === 'fulfilled') setIndexing(indexingRes.value);

        // Check if we have any real data
        const ga4Data = ga4Res.status === 'fulfilled' ? ga4Res.value : null;
        setHasData(ga4Data && (ga4Data.pageviews > 0 || ga4Data.total_users > 0));
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchSEO();
  }, [getAuthToken]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>
        <ChartSkeleton />
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="space-y-4">
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-6 shadow-sm text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-indigo-100 rounded-2xl mb-4">
            <Search className="w-7 h-7 text-indigo-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">SEO Analytics</h3>
          <p className="text-sm text-gray-500 mb-4">
            Connect Google Analytics 4 and Search Console to see SEO data.
          </p>
          <p className="text-xs text-gray-400">
            Set GA4_PROPERTY_ID, GOOGLE_CREDENTIALS_BASE64, and GSC_SITE_URL environment variables.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* GA4 Overview Cards */}
      {ga4 && (
        <div className="grid grid-cols-2 gap-3">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-blue-100"><Eye className="w-3.5 h-3.5 text-blue-600" /></div>
              <p className="text-[10px] font-semibold uppercase text-gray-400">Pageviews</p>
            </div>
            <p className="text-2xl font-extrabold text-gray-900">{ga4.pageviews.toLocaleString()}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-purple-100"><Users className="w-3.5 h-3.5 text-purple-600" /></div>
              <p className="text-[10px] font-semibold uppercase text-gray-400">Users</p>
            </div>
            <p className="text-2xl font-extrabold text-gray-900">{ga4.total_users.toLocaleString()}</p>
            <p className="text-[10px] text-gray-400">{ga4.new_users} new</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-emerald-100"><TrendingUp className="w-3.5 h-3.5 text-emerald-600" /></div>
              <p className="text-[10px] font-semibold uppercase text-gray-400">Engagement</p>
            </div>
            <p className="text-2xl font-extrabold text-gray-900">{ga4.engagement_rate}%</p>
            <p className="text-[10px] text-gray-400">Bounce: {ga4.bounce_rate}%</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-amber-100"><Globe className="w-3.5 h-3.5 text-amber-600" /></div>
              <p className="text-[10px] font-semibold uppercase text-gray-400">Sessions</p>
            </div>
            <p className="text-2xl font-extrabold text-gray-900">{ga4.sessions.toLocaleString()}</p>
            <p className="text-[10px] text-gray-400">Avg: {Math.round(ga4.avg_session_duration)}s</p>
          </motion.div>
        </div>
      )}

      {/* Search Console Metrics */}
      {indexing && (
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Indexing Coverage</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-500">Indexed</span>
                <span className="font-bold text-emerald-600">{indexing.indexed}</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 rounded-full"
                  style={{ width: `${indexing.coverage_rate || 0}%` }}
                />
              </div>
              <div className="flex justify-between text-[10px] mt-1">
                <span className="text-gray-400">{indexing.coverage_rate || 0}% coverage</span>
                <span className="text-gray-400">{indexing.not_indexed} pending</span>
              </div>
            </div>
            <FileCheck className="w-8 h-8 text-emerald-200" />
          </div>
        </div>
      )}

      {/* Top Search Queries */}
      {queries.length > 0 && (
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Search Queries</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 font-semibold text-gray-500">Query</th>
                  <th className="text-right py-2 font-semibold text-gray-500">Clicks</th>
                  <th className="text-right py-2 font-semibold text-gray-500">Impr.</th>
                  <th className="text-right py-2 font-semibold text-gray-500">CTR</th>
                  <th className="text-right py-2 font-semibold text-gray-500">Pos.</th>
                </tr>
              </thead>
              <tbody>
                {queries.slice(0, 10).map((q) => (
                  <tr key={q.query} className="border-b border-gray-50">
                    <td className="py-1.5 text-gray-700 max-w-[120px] truncate">{q.query}</td>
                    <td className="py-1.5 text-right font-bold text-gray-900">{q.clicks}</td>
                    <td className="py-1.5 text-right text-gray-500">{q.impressions.toLocaleString()}</td>
                    <td className="py-1.5 text-right text-gray-500">{q.ctr}%</td>
                    <td className="py-1.5 text-right text-gray-500">{q.position}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Pages Bar Chart */}
      {pages.length > 0 && (
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Pages (Clicks)</h3>
          <ResponsiveContainer width="100%" height={Math.max(150, Math.min(pages.length, 8) * 28)}>
            <BarChart data={pages.slice(0, 8)} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis
                dataKey="path"
                type="category"
                tick={{ fontSize: 8 }}
                width={100}
                tickFormatter={(v: string) => {
                  const short = v.replace(/^https?:\/\/[^/]+/, '');
                  return short.length > 20 ? short.slice(0, 20) + '...' : short;
                }}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              />
              <Bar dataKey="clicks" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
